---
id: interview
title: Interview service
owner: Backend Team
status: Active
lastReviewed: 2026-02-02
visibility: internal
tags:
  - interview
  - ai
  - microservice
  - analysis
---

## Product Overview

### Who it serves

This microservice supports the AI interview experience for authenticated users by creating interview sessions, coordinating live interview rooms, and returning interview artifacts like videos, transcripts, and analysis.

### Primary workflow

Clients create or update interview records, then start a LiveKit room for the interview session. As recordings land in cloud storage and transcription completes, webhook callbacks mark processing stages and trigger analysis once audio, video, and transcript inputs are ready.

### Business rules

Interview endpoints under `/v1/interviews` require Firebase authentication, enforce ownership for updates and deletes, and limit free interview creation unless the user has an active subscription. Resume references are validated against the resume store before being attached to interviews.

### Outcomes

The service persists interview state in Firestore, stores interview assets in GCS, produces transcripts and analysis metrics (pace, tone, speaking time, filler words, quick fillers), and exposes download or streaming URLs for completed interviews.

### Out of scope

Client UI, billing/subscription management, and resume parsing are handled by other services.

### API documentation

[Internal API Documentation](https://wiki.rezi.ai/apis/interview-api-docs)

## Technical Overview

### Architecture flow

Routes defined in `apps/micro-services/interview/pkg/api/routes/routes.go` wire the interview controller to interview services that orchestrate Firestore repositories, LiveKit rooms, storage access, transcription, and analysis.

### Entry points

- The API server boots from `apps/micro-services/interview/cmd/interview/main.go`
- OpenAPI generation is performed by `apps/micro-services/interview/cmd/spec/main.go` using route metadata.

### Key modules

Request handling lives in `apps/micro-services/interview/pkg/api/controllers`, core interview workflows and webhook handlers in `apps/micro-services/interview/pkg/services`, and analysis logic in `apps/micro-services/interview/pkg/services/interviewanalyzer` with Gemini-backed tone analysis.

### Data sources and sinks

Interview state, transcripts, and analysis results are stored in Firestore repositories under `apps/micro-services/interview/pkg/repo/nosql/firestore`, while recordings and thumbnails are read from and written to GCS via the storage adapter.

### Integrations and side effects

The service integrates with Firebase Auth, LiveKit webhooks and room management, AssemblyAI transcription, the DB API service for job post enrichment, Gemini-based tone analysis, and optional Amplitude event logging.

### Constraints and risks

Processing depends on asynchronous webhooks for storage and transcription, and availability of external providers (LiveKit, AssemblyAI, Gemini, GCS). Correct environment configuration and API keys are required to avoid dropped callbacks or analysis gaps.

## Changelog

- 2026-02-03: Can have custom token
- 2026-02-02: Initial documentation.
